import { useState, useEffect, useMemo, Fragment } from "react";
import { apiClient, useAuth, API } from "../App";
import { useAdminControls } from "../AdminMode";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import {
  Plus, Search, Trash2, Edit, Music, Clock, User, FileText, Upload,
  PackageOpen, Undo2, Download, Library, ChevronDown, ChevronRight,
} from "lucide-react";
import ImportDialog from "../components/ImportDialog";
import SortableTableHead from "../components/SortableTableHead";
import useTableControls from "../hooks/useTableControls";
import useScrollRestoration from "../hooks/useScrollRestoration";

const fmtDateUK = (iso) => {
  if (!iso) return "";
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split("-");
  return y && m && d ? `${d}/${m}/${y}` : s;
};

// Iter-69: column config for sort + per-column filter dropdowns.
const MUSIC_COLUMNS = [
  { key: "number", label: "Number", accessor: "number", sortable: true, filterable: false },
  { key: "title", label: "Title", accessor: "title", sortable: true, filterable: false },
  { key: "composer", label: "Composer", accessor: "composer", sortable: true, filterable: true },
  { key: "arranger", label: "Arranger", accessor: "arranger", sortable: true, filterable: true },
  { key: "genre", label: "Genre", accessor: "genre", sortable: true, filterable: true },
  { key: "solo", label: "Solo", accessor: "solo", sortable: true, filterable: false },
  { key: "notes", label: "Comments", accessor: "notes", sortable: false, filterable: false },
  { key: "location", label: "Location", accessor: (r) => (r.in_pads ? "In pads" : "In library"), sortable: true, filterable: true },
];

const MusicLibrary = () => {
  const { user } = useAuth();
  const [pieces, setPieces] = useState([]);
  const [loading, setLoading] = useState(true);
  // iter-86: keep scroll position when admin edits a piece via the dialog
  // (also kicks in when navigating away to event detail and back)
  useScrollRestoration({ ready: !loading });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [editingPiece, setEditingPiece] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [location, setLocation] = useState("all"); // all | library | pads
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [handInDialogOpen, setHandInDialogOpen] = useState(false);
  const [handInNotify, setHandInNotify] = useState(true);
  const [formData, setFormData] = useState({
    number: "",
    title: "",
    composer: "",
    arranger: "",
    genre: "",
    solo: "",
    notes: ""
  });

  const isAdmin = user?.role === "admin";
  const { showAdminControls } = useAdminControls();

  useEffect(() => {
    fetchPieces();
  }, []);

  const fetchPieces = async () => {
    try {
      const res = await apiClient.get("/music");
      setPieces(res.data);
    } catch (error) {
      toast.error("Failed to load music library");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // iter-85: send empty strings rather than null so the backend's
      // `if v is not None` filter on PUT /music/{id} doesn't strip
      // cleared fields. Admins clearing the arranger after an import was
      // previously a no-op — the change persists now.
      const data = {
        title: formData.title,
        composer: formData.composer,
        number: formData.number || "",
        arranger: formData.arranger || "",
        genre: formData.genre || "",
        solo: formData.solo || "",
        notes: formData.notes || "",
      };

      if (editingPiece) {
        await apiClient.put(`/music/${editingPiece.id}`, data);
        toast.success("Music piece updated");
      } else {
        await apiClient.post("/music", data);
        toast.success("Music piece added");
      }
      setDialogOpen(false);
      setEditingPiece(null);
      resetForm();
      fetchPieces();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save music piece");
    }
  };

  const handleDelete = async (pieceId) => {
    if (!confirm("Are you sure you want to delete this music piece?")) return;
    try {
      await apiClient.delete(`/music/${pieceId}`);
      toast.success("Music piece deleted");
      fetchPieces();
    } catch (error) {
      toast.error("Failed to delete music piece");
    }
  };

  const handleEdit = (piece) => {
    setEditingPiece(piece);
    setFormData({
      number: piece.number || "",
      title: piece.title,
      composer: piece.composer,
      arranger: piece.arranger || "",
      genre: piece.genre || "",
      solo: piece.solo || "",
      notes: piece.notes || ""
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      number: "",
      title: "",
      composer: "",
      arranger: "",
      genre: "",
      solo: "",
      notes: ""
    });
  };

  const filteredPieces = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return pieces.filter((piece) => {
      if (location === "library" && piece.in_pads) return false;
      if (location === "pads" && !piece.in_pads) return false;
      if (!q) return true;
      return (
        piece.title.toLowerCase().includes(q) ||
        piece.composer.toLowerCase().includes(q) ||
        (piece.arranger && piece.arranger.toLowerCase().includes(q)) ||
        (piece.number && piece.number.toLowerCase().includes(q)) ||
        (piece.genre && piece.genre.toLowerCase().includes(q)) ||
        (piece.solo && piece.solo.toLowerCase().includes(q))
      );
    });
  }, [pieces, location, searchTerm]);

  // Iter-69→70: sort + per-column filter state on top of search/location
  // filter. Default sort: Title ascending (iter-70 user request).
  const musicCtl = useTableControls(filteredPieces, MUSIC_COLUMNS, { defaultSortKey: "title", defaultSortDir: "asc" });

  // Iter-76: A-Z collapsible sections. Bucket char comes from the FIRST
  // letter of whatever column is the active sort key — so sorting by
  // composer regroups by composer's first letter. Digits → "#", anything
  // else (e.g. punctuation, accented letters that don't normalise) → "?".
  const [collapsedBuckets, setCollapsedBuckets] = useState(() => new Set());

  const bucketFor = (piece) => {
    const key = musicCtl.sortKey || "title";
    const raw = (piece[key] || piece.title || "").toString().trim();
    if (!raw) return "?";
    const ch = raw[0].toUpperCase();
    if (/[A-Z]/.test(ch)) return ch;
    if (/[0-9]/.test(ch)) return "#";
    return "?";
  };

  const groupedRows = useMemo(() => {
    // Build [{bucket: 'A', rows: [...]}, ...] in the same order as the
    // processedRows so the rendered order matches the active sort direction.
    const buckets = new Map();
    for (const piece of musicCtl.processedRows) {
      const b = bucketFor(piece);
      if (!buckets.has(b)) buckets.set(b, []);
      buckets.get(b).push(piece);
    }
    return Array.from(buckets.entries()).map(([bucket, rows]) => ({ bucket, rows }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [musicCtl.processedRows, musicCtl.sortKey]);

  const toggleBucket = (b) => {
    setCollapsedBuckets((prev) => {
      const next = new Set(prev);
      if (next.has(b)) next.delete(b);
      else next.add(b);
      return next;
    });
  };

  const allBucketsCollapsed = groupedRows.length > 0 && groupedRows.every((g) => collapsedBuckets.has(g.bucket));
  const setAllBuckets = (collapse) => {
    setCollapsedBuckets(collapse ? new Set(groupedRows.map((g) => g.bucket)) : new Set());
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const bulkHandOut = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      const r = await apiClient.post("/music/hand-out", { music_ids: ids });
      toast.success(r.data?.message || "Handed out");
      clearSelection();
      fetchPieces();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to hand out");
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkHandIn = async (notifyMembers = false) => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      const r = await apiClient.post("/music/hand-in", {
        music_ids: ids,
        notify_members: notifyMembers,
      });
      toast.success(r.data?.message || "Returned to library");
      clearSelection();
      fetchPieces();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to hand in");
    } finally {
      setBulkBusy(false);
    }
  };

  const openHandInDialog = () => {
    const inPadsCount = filteredPieces.filter((p) => selected.has(p.id) && p.in_pads).length;
    if (inPadsCount === 0) return;
    setHandInNotify(true);
    setHandInDialogOpen(true);
  };

  const downloadPadsCsv = async () => {
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${API}/music/pads.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "pads.csv";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error("Failed to download CSV");
    }
  };

  // Counts for the tab badges (ignore search — these are location totals)
  const padCount = useMemo(() => pieces.filter((p) => p.in_pads).length, [pieces]);
  const libraryCount = useMemo(() => pieces.filter((p) => !p.in_pads).length, [pieces]);

  // Selection is only meaningful within the current tab — auto-drop IDs that aren't visible
  const visibleSelected = useMemo(
    () => new Set(filteredPieces.filter((p) => selected.has(p.id)).map((p) => p.id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredPieces, selected]
  );

  const selectedInPadsCount = useMemo(
    () => filteredPieces.filter((p) => selected.has(p.id) && p.in_pads).length,
    [filteredPieces, selected]
  );
  const selectedInLibraryCount = useMemo(
    () => filteredPieces.filter((p) => selected.has(p.id) && !p.in_pads).length,
    [filteredPieces, selected]
  );

  const allVisibleSelected =
    filteredPieces.length > 0 && filteredPieces.every((p) => selected.has(p.id));

  const toggleSelectAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        filteredPieces.forEach((p) => next.delete(p.id));
      } else {
        filteredPieces.forEach((p) => next.add(p.id));
      }
      return next;
    });
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case "easy": return "bg-green-500/20 text-green-400";
      case "medium": return "bg-yellow-500/20 text-yellow-400";
      case "hard": return "bg-orange-500/20 text-orange-400";
      case "advanced": return "bg-red-500/20 text-red-400";
      default: return "bg-muted text-muted-foreground";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-primary text-xl font-heading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight">Music Library</h1>
          <p className="text-muted-foreground mt-1">Manage your band's repertoire</p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap w-full sm:w-auto">
          <div className="relative hidden sm:block sm:flex-initial min-w-[160px]" data-testid="search-music-wrapper-desktop">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search music..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 w-full sm:w-64 bg-secondary/50"
              data-testid="search-music-input"
            />
          </div>
          
          {showAdminControls && (
            <>
              <Button 
                variant="outline" 
                className="border-white/[0.06] h-10"
                onClick={() => setImportDialogOpen(true)}
                data-testid="import-music-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Import CSV
              </Button>
              <Dialog open={dialogOpen} onOpenChange={(open) => {
                setDialogOpen(open);
                if (!open) {
                  setEditingPiece(null);
                  resetForm();
                }
              }}>
                <DialogTrigger asChild>
                  <Button className="bg-primary text-primary-foreground btn-glow h-10" data-testid="add-music-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Music
                  </Button>
                </DialogTrigger>
              <DialogContent
                className="glass border-white/10 max-w-lg"
                /* iter-86: Radix returns focus to the trigger button on close,
                   which scrolls the page if the trigger is off-screen. Cancel
                   the auto-scroll so the table stays where the admin left it. */
                onCloseAutoFocus={(e) => e.preventDefault()}
              >
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl">
                    {editingPiece ? "Edit Music Piece" : "Add New Music"}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-2">
                      <Label>Number</Label>
                      <Input
                        data-testid="music-number-input"
                        placeholder="e.g. 0042"
                        value={formData.number}
                        onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                        className="bg-secondary/50"
                      />
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label>Title *</Label>
                      <Input
                        data-testid="music-title-input"
                        placeholder="e.g., Floral Dance"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        className="bg-secondary/50"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Composer *</Label>
                      <Input
                        data-testid="music-composer-input"
                        placeholder="e.g., Katie Moss"
                        value={formData.composer}
                        onChange={(e) => setFormData({ ...formData, composer: e.target.value })}
                        className="bg-secondary/50"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Arranger</Label>
                      <Input
                        data-testid="music-arranger-input"
                        placeholder="e.g., Howard Snell"
                        value={formData.arranger}
                        onChange={(e) => setFormData({ ...formData, arranger: e.target.value })}
                        className="bg-secondary/50"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Genre</Label>
                      <Input
                        data-testid="music-genre-input"
                        placeholder="e.g., March, Hymn, Test piece"
                        value={formData.genre}
                        onChange={(e) => setFormData({ ...formData, genre: e.target.value })}
                        className="bg-secondary/50"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Solo (instrumentation)</Label>
                      <Input
                        data-testid="music-solo-input"
                        placeholder="e.g., Bb Cornet, Trombone, Euphonium"
                        value={formData.solo}
                        onChange={(e) => setFormData({ ...formData, solo: e.target.value })}
                        className="bg-secondary/50"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Comments</Label>
                    <Textarea
                      data-testid="music-notes-input"
                      placeholder="Add any comments about this piece..."
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className="bg-secondary/50 min-h-[80px]"
                    />
                  </div>
                  
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-primary text-primary-foreground" data-testid="save-music-btn">
                      {editingPiece ? "Update" : "Add Music"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            
            <ImportDialog
              open={importDialogOpen}
              onOpenChange={setImportDialogOpen}
              importType="music"
              onSuccess={fetchPieces}
            />
            </>
          )}
        </div>
      </div>

      {/* Stats — compact on mobile, full size on md+ */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
        <Card className="glass-card">
          <CardContent className="p-3 sm:p-6">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-[11px] sm:text-sm text-muted-foreground">Total Pieces</p>
                <p className="text-xl sm:text-3xl font-heading font-bold mt-0.5 sm:mt-1">{pieces.length}</p>
              </div>
              <div className="w-8 h-8 sm:w-12 sm:h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Music className="w-4 h-4 sm:w-6 sm:h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="glass-card">
          <CardContent className="p-3 sm:p-6">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-[11px] sm:text-sm text-muted-foreground">Composers</p>
                <p className="text-xl sm:text-3xl font-heading font-bold mt-0.5 sm:mt-1">
                  {new Set(pieces.map(p => p.composer)).size}
                </p>
              </div>
              <div className="w-8 h-8 sm:w-12 sm:h-12 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                <User className="w-4 h-4 sm:w-6 sm:h-6 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="glass-card">
          <CardContent className="p-3 sm:p-6">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-[11px] sm:text-sm text-muted-foreground">Total Duration</p>
                <p className="text-xl sm:text-3xl font-heading font-bold mt-0.5 sm:mt-1">
                  {pieces.reduce((acc, p) => acc + (p.duration_minutes || 0), 0)} <span className="text-xs sm:text-base font-normal text-muted-foreground">min</span>
                </p>
              </div>
              <div className="w-8 h-8 sm:w-12 sm:h-12 rounded-lg bg-green-500/10 flex items-center justify-center flex-shrink-0">
                <Clock className="w-4 h-4 sm:w-6 sm:h-6 text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="glass-card">
          <CardContent className="p-3 sm:p-6">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-[11px] sm:text-sm text-muted-foreground">With Arrangements</p>
                <p className="text-xl sm:text-3xl font-heading font-bold mt-0.5 sm:mt-1">
                  {pieces.filter(p => p.arranger).length}
                </p>
              </div>
              <div className="w-8 h-8 sm:w-12 sm:h-12 rounded-lg bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                <FileText className="w-4 h-4 sm:w-6 sm:h-6 text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Mobile-only search bar — sits right above the list (desktop has the one in the header) */}
      <div className="relative sm:hidden" data-testid="search-music-wrapper-mobile">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search music..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-9 w-full bg-secondary/50"
          data-testid="search-music-input-mobile"
        />
      </div>

      {/* Tabs + bulk action bar */}
      <Card className="glass-card">
        <CardHeader className="border-b border-white/5 pb-4 space-y-3">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <Tabs value={location} onValueChange={(v) => { setLocation(v); clearSelection(); }}>
              <TabsList data-testid="music-location-tabs">
                <TabsTrigger value="all" data-testid="tab-all">
                  All <Badge variant="outline" className="ml-2 text-[10px]">{pieces.length}</Badge>
                </TabsTrigger>
                <TabsTrigger value="library" data-testid="tab-library">
                  <Library className="w-3.5 h-3.5 mr-1" /> In Library
                  <Badge variant="outline" className="ml-2 text-[10px]">{libraryCount}</Badge>
                </TabsTrigger>
                <TabsTrigger value="pads" data-testid="tab-pads">
                  <PackageOpen className="w-3.5 h-3.5 mr-1" /> In Pads
                  <Badge
                    variant="outline"
                    className={`ml-2 text-[10px] ${padCount > 0 ? "bg-amber-500/15 text-amber-300 border-transparent" : ""}`}
                  >
                    {padCount}
                  </Badge>
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="flex items-center gap-2 flex-wrap">
              {groupedRows.length > 1 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAllBuckets(!allBucketsCollapsed)}
                  className="border-white/10 text-xs"
                  data-testid="bucket-toggle-all"
                >
                  {allBucketsCollapsed ? (
                    <><ChevronDown className="w-3 h-3 mr-1" /> Expand all</>
                  ) : (
                    <><ChevronRight className="w-3 h-3 mr-1" /> Collapse all</>
                  )}
                </Button>
              )}
              {showAdminControls && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={downloadPadsCsv}
                  disabled={padCount === 0}
                  data-testid="pads-csv-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Pads CSV
                </Button>
              )}
            </div>
          </div>

          {showAdminControls && visibleSelected.size > 0 && (
            <div
              className="flex items-center justify-between gap-3 flex-wrap rounded-md border border-primary/30 bg-primary/5 p-3"
              data-testid="bulk-action-bar"
            >
              <p className="text-sm">
                <strong data-testid="bulk-selected-count">{visibleSelected.size}</strong> piece
                {visibleSelected.size === 1 ? "" : "s"} selected
                {selectedInLibraryCount > 0 && ` (${selectedInLibraryCount} in library)`}
                {selectedInPadsCount > 0 && ` (${selectedInPadsCount} in pads)`}
              </p>
              <div className="flex gap-2 flex-wrap">
                {selectedInLibraryCount > 0 && (
                  <Button
                    size="sm"
                    onClick={bulkHandOut}
                    disabled={bulkBusy}
                    data-testid="bulk-hand-out-btn"
                  >
                    <PackageOpen className="w-4 h-4 mr-2" />
                    Hand out to pads ({selectedInLibraryCount})
                  </Button>
                )}
                {selectedInPadsCount > 0 && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={openHandInDialog}
                    disabled={bulkBusy}
                    data-testid="bulk-hand-in-btn"
                  >
                    <Undo2 className="w-4 h-4 mr-2" />
                    Hand back to library ({selectedInPadsCount})
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={clearSelection} data-testid="bulk-clear-btn">
                  Clear
                </Button>
              </div>
            </div>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {filteredPieces.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="border-white/5 hover:bg-transparent">
                  {showAdminControls && (
                    <TableHead className="w-10">
                      <Checkbox
                        checked={allVisibleSelected}
                        onCheckedChange={toggleSelectAllVisible}
                        data-testid="select-all-checkbox"
                      />
                    </TableHead>
                  )}
                  {MUSIC_COLUMNS.map((col) => (
                    <SortableTableHead
                      key={col.key}
                      column={col}
                      sortKey={musicCtl.sortKey}
                      sortDir={musicCtl.sortDir}
                      onToggleSort={() => musicCtl.toggleSort(col.key)}
                      rows={filteredPieces}
                      selectedFilters={musicCtl.filters[col.key]}
                      onChangeFilter={(set) => musicCtl.setColumnFilter(col.key, set)}
                      className={col.key === "number" ? "w-20" : undefined}
                    />
                  ))}
                  {showAdminControls && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {groupedRows.map(({ bucket, rows }) => {
                  const collapsed = collapsedBuckets.has(bucket);
                  // Total columns: checkbox (admin) + 7 data cols + actions (admin) — used for the
                  // bucket header row colspan so the heading spans the full table width.
                  const colSpan = (showAdminControls ? 2 : 0) + 7;
                  return (
                  <Fragment key={`bucket-${bucket}`}>
                    <TableRow
                      className="border-white/5 hover:bg-primary/[0.08] bg-primary/[0.04] cursor-pointer"
                      onClick={() => toggleBucket(bucket)}
                      data-testid={`bucket-header-${bucket}`}
                    >
                      <TableCell colSpan={colSpan} className="py-2 px-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {collapsed ? (
                              <ChevronRight className="w-4 h-4 text-primary" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-primary" />
                            )}
                            <span className="text-sm font-semibold uppercase tracking-wider text-primary">
                              {bucket}
                            </span>
                          </div>
                          <span className="text-[11px] text-muted-foreground">
                            {rows.length} {rows.length === 1 ? "piece" : "pieces"}
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                    {!collapsed && rows.map((piece) => {
                      const checked = selected.has(piece.id);
                      return (
                  <TableRow
                    key={piece.id}
                    className={`border-white/5 ${checked ? "bg-primary/5" : ""}`}
                    data-testid={`music-row-${piece.id}`}
                  >
                    {showAdminControls && (
                      <TableCell>
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => toggleSelect(piece.id)}
                          data-testid={`select-music-${piece.id}`}
                        />
                      </TableCell>
                    )}
                    <TableCell className="font-mono text-sm text-muted-foreground">
                      {piece.number || "—"}
                    </TableCell>
                    <TableCell className="font-medium">{piece.title}</TableCell>
                    <TableCell>{piece.composer}</TableCell>
                    <TableCell>
                      {piece.arranger || <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell>
                      {piece.genre || <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="max-w-[180px]">
                      {piece.solo ? (
                        <span className="text-sm">{piece.solo}</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[240px]">
                      {piece.notes ? (
                        <span className="text-xs text-muted-foreground line-clamp-2" title={piece.notes}>
                          {piece.notes}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {piece.in_pads ? (
                        <div className="space-y-0.5">
                          <Badge
                            variant="outline"
                            className="bg-amber-500/15 text-amber-300 border-transparent text-[10px]"
                            data-testid={`pads-badge-${piece.id}`}
                          >
                            <PackageOpen className="w-3 h-3 mr-1" />
                            In pads
                          </Badge>
                          {piece.handed_out_at && (
                            <p className="text-[11px] text-muted-foreground">
                              since {fmtDateUK(piece.handed_out_at)}
                              {piece.handed_out_by_name ? ` · ${piece.handed_out_by_name}` : ""}
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-0.5">
                          <Badge
                            variant="outline"
                            className="bg-emerald-500/10 text-emerald-300 border-transparent text-[10px]"
                          >
                            <Library className="w-3 h-3 mr-1" />
                            In library
                          </Badge>
                          {piece.last_in_pads_at && (
                            <p
                              className="text-[11px] text-muted-foreground"
                              data-testid={`last-in-pads-${piece.id}`}
                            >
                              Last in pads: {fmtDateUK(piece.last_in_pads_at)}
                            </p>
                          )}
                        </div>
                      )}
                    </TableCell>
                    {showAdminControls && (
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(piece)}
                            data-testid={`edit-music-${piece.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleDelete(piece.id)}
                            data-testid={`delete-music-${piece.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                );})}
                  </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Music className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>
                {searchTerm
                  ? "No music found matching your search"
                  : location === "pads"
                    ? "No pieces are currently in pads."
                    : location === "library"
                      ? "No pieces are currently in the library."
                      : "No music in the library yet"}
              </p>
              {showAdminControls && !searchTerm && location === "all" && (
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => setDialogOpen(true)}
                >
                  Add Your First Piece
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Hand-in confirm dialog — offers optional member-notification email */}
      <Dialog open={handInDialogOpen} onOpenChange={setHandInDialogOpen}>
        <DialogContent className="max-w-md" data-testid="hand-in-dialog">
          <DialogHeader>
            <DialogTitle>Hand back to the library</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Returning <strong className="text-foreground">{selectedInPadsCount}</strong> piece
              {selectedInPadsCount === 1 ? "" : "s"} to the library. Each piece will be stamped
              with today&apos;s date as its last-in-pads entry.
            </p>
            <div className="rounded-md border border-white/10 bg-white/[0.02] p-3 max-h-40 overflow-y-auto">
              <ul className="text-sm space-y-1">
                {filteredPieces
                  .filter((p) => selected.has(p.id) && p.in_pads)
                  .map((p) => (
                    <li key={p.id} className="flex items-center gap-2">
                      <Undo2 className="w-3 h-3 text-amber-400 flex-shrink-0" />
                      <span className="truncate">
                        <strong>{p.title}</strong>
                        {p.composer ? <span className="text-muted-foreground"> — {p.composer}</span> : null}
                      </span>
                    </li>
                  ))}
              </ul>
            </div>
            <label className="flex items-start gap-3 cursor-pointer select-none rounded-md border border-white/10 p-3 hover:bg-white/[0.02]">
              <Checkbox
                checked={handInNotify}
                onCheckedChange={(v) => setHandInNotify(!!v)}
                className="mt-0.5"
                data-testid="hand-in-notify-checkbox"
              />
              <div className="text-sm">
                <p className="font-medium">Email band members</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Send an email to all members listing the pieces being collected so they
                  know what to bring back to the next rehearsal.
                </p>
              </div>
            </label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setHandInDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={async () => {
                setHandInDialogOpen(false);
                await bulkHandIn(handInNotify);
              }}
              disabled={bulkBusy}
              data-testid="hand-in-confirm-btn"
            >
              <Undo2 className="w-4 h-4 mr-2" />
              {handInNotify ? "Return & email members" : "Return to library"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MusicLibrary;
